import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import env from '../config/environment';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  Users, DollarSign, Activity, 
  UserCheck, AlertCircle, Settings, CreditCard, TrendingUp, Webhook, Mail, Shield
} from 'lucide-react';
import RefundManagement from '../components/RefundManagement';
import { WebhookManagement } from '../components/WebhookManagement';
import { EmailQueueManagement } from '../components/EmailQueueManagement';
import BlacklistManagement from '../components/BlacklistManagement';

// Types
interface DashboardSummary {
  total_users: number;
  new_users_30d: number;
  admin_count: number;
  active_subscriptions: number;
  paid_subscriptions: number;
  mrr: number;
  current_month_revenue: number;
  last_month_revenue: number;
  daily_active_users: number;
  pending_plan_changes: number;
  recent_cancellations: number;
}

interface RevenueTrend {
  month: string;
  revenue: number;
  unique_customers: number;
  transaction_count: number;
  personal_revenue: number;
  business_revenue: number;
  enterprise_revenue: number;
  success_rate: number;
  mom_growth_percent: number;
}

interface SubscriptionAnalytics {
  plan_id: string;
  plan_name: string;
  price_monthly: number;
  subscriber_count: number;
  mrr_contribution: number;
  active_count: number;
  cancelled_count: number;
  recent_cancellations: number;
  pending_changes: number;
  avg_lifetime_days: number;
}

interface UsageAnalytics {
  usage_date: string;
  plan_name: string;
  active_users: number;
  total_messages: number;
  total_video_minutes: number;
  message_limit_utilization: number;
  video_limit_utilization: number;
  users_hit_message_limit: number;
  users_hit_video_limit: number;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userRole, setUserRole] = useState<'admin' | 'super_admin' | null>(null);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'refunds' | 'webhooks' | 'emails' | 'blacklist'>('dashboard');
  const [dashboardData, setDashboardData] = useState<{
    summary: DashboardSummary | null;
    revenue_trends: RevenueTrend[];
    subscription_analytics: SubscriptionAnalytics[];
    usage_analytics: UsageAnalytics[];
  }>({
    summary: null,
    revenue_trends: [],
    subscription_analytics: [],
    usage_analytics: []
  });
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  useEffect(() => {
    checkAdminAccess();
  }, []);

  useEffect(() => {
    if (isAdmin && activeTab === 'dashboard') {
      loadDashboardData();
    }
  }, [isAdmin, selectedPeriod, activeTab]);

  const checkAdminAccess = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/login');
        return;
      }

      // Determine table prefix based on environment
      const tablePrefix = env.isProduction ? 'P_' : 'D_';
      const profilesTable = `${tablePrefix}profiles`;

      // Check if user is admin using RPC function
      const { data: isAdminUser, error } = await supabase.rpc('is_user_admin');
      
      if (error || !isAdminUser) {
        // Fallback to direct check
        const { data: profile } = await supabase
          .from(profilesTable)
          .select('role')
          .eq('id', user.id)
          .single();

        if (!profile || !['admin', 'super_admin'].includes(profile.role)) {
          navigate('/');
          return;
        }
        setUserRole(profile.role as 'admin' | 'super_admin');
      } else {
        // Get the actual role for RPC success case
        const { data: profile } = await supabase
          .from(profilesTable)
          .select('role')
          .eq('id', user.id)
          .single();
        
        if (profile) {
          setUserRole(profile.role as 'admin' | 'super_admin');
        }
      }

      setIsAdmin(true);
    } catch (error) {
      console.error('Error checking admin access:', error);
      navigate('/');
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Determine table prefix based on environment
      const tablePrefix = env.isProduction ? 'P_' : 'D_';

      // Load dashboard summary
      const { data: summary } = await supabase
        .from(`${tablePrefix}view_admin_dashboard_summary`)
        .select('*')
        .single();

      // Load revenue trends
      const { data: revenueTrends } = await supabase
        .from(`${tablePrefix}view_admin_revenue_trends`)
        .select('*')
        .order('month', { ascending: false })
        .limit(12);

      // Load subscription analytics
      const { data: subscriptions } = await supabase
        .from(`${tablePrefix}view_admin_subscription_analytics`)
        .select('*');

      // Load usage analytics based on selected period
      const daysAgo = selectedPeriod === '7d' ? 7 : 
                      selectedPeriod === '30d' ? 30 : 
                      selectedPeriod === '90d' ? 90 : 365;
      
      const { data: usage } = await supabase
        .from(`${tablePrefix}view_admin_usage_analytics`)
        .select('*')
        .gte('usage_date', new Date(Date.now() - daysAgo * 24 * 60 * 60 * 1000).toISOString())
        .order('usage_date', { ascending: false });

      setDashboardData({
        summary: summary || null,
        revenue_trends: revenueTrends || [],
        subscription_analytics: subscriptions || [],
        usage_analytics: usage || []
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const { summary, revenue_trends, subscription_analytics, usage_analytics } = dashboardData;

  // Calculate MRR growth
  const mrrGrowth = summary && summary.last_month_revenue 
    ? ((summary.current_month_revenue - summary.last_month_revenue) / summary.last_month_revenue * 100).toFixed(1)
    : '0';

  // Prepare chart data
  const revenueChartData = revenue_trends.map(trend => ({
    month: formatDate(trend.month),
    revenue: trend.revenue,
    personal: trend.personal_revenue,
    business: trend.business_revenue,
    enterprise: trend.enterprise_revenue
  })).reverse();

  const subscriptionPieData = subscription_analytics
    .filter(s => s.subscriber_count > 0)
    .map(s => ({
      name: s.plan_name,
      value: s.subscriber_count,
      mrr: s.mrr_contribution
    }));

  const usageByPlan = usage_analytics.reduce((acc, curr) => {
    if (!acc[curr.plan_name]) {
      acc[curr.plan_name] = {
        messages: 0,
        video: 0,
        users: new Set()
      };
    }
    acc[curr.plan_name].messages += curr.total_messages;
    acc[curr.plan_name].video += curr.total_video_minutes;
    acc[curr.plan_name].users.add(curr.active_users);
    return acc;
  }, {} as Record<string, any>);

  const usageChartData = Object.entries(usageByPlan).map(([plan, data]) => ({
    plan,
    messages: data.messages,
    video: data.video,
    users: data.users.size
  }));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <Settings className="h-6 w-6 text-gray-600" />
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              {activeTab === 'dashboard' && (
                <select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value as any)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="1y">Last year</option>
                </select>
              )}
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Go back
              </button>
            </div>
          </div>
          {/* Tab Navigation */}
          <div className="flex space-x-1 border-t border-gray-200">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'dashboard'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-4 w-4" />
                <span>Analytics Dashboard</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('refunds')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'refunds'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <CreditCard className="h-4 w-4" />
                <span>Refund Management</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('webhooks')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'webhooks'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Webhook className="h-4 w-4" />
                <span>Webhook Management</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('emails')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'emails'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Mail className="h-4 w-4" />
                <span>Email Queue</span>
              </div>
            </button>
            <button
              onClick={() => setActiveTab('blacklist')}
              className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'blacklist'
                  ? 'text-blue-600 border-blue-600'
                  : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <Shield className="h-4 w-4" />
                <span>블랙 관리</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'webhooks' ? (
          <WebhookManagement 
            userRole={userRole}
          />
        ) : activeTab === 'refunds' ? (
          <RefundManagement 
            supabaseUrl={env.supabaseUrl}
            supabaseAnonKey={env.supabaseAnonKey}
            isKorean={false}
            userRole={userRole}
          />
        ) : activeTab === 'emails' ? (
          <EmailQueueManagement 
            supabase={supabase}
            isDevelopment={env.isDevelopment}
            userRole={userRole}
          />
        ) : activeTab === 'blacklist' ? (
          <BlacklistManagement
            supabaseUrl={env.supabaseUrl}
            supabaseAnonKey={env.supabaseAnonKey}
            isKorean={false}
            userRole={userRole}
          />
        ) : (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(summary?.total_users || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  +{formatNumber(summary?.new_users_30d || 0)} last 30d
                </p>
              </div>
              <Users className="h-12 w-12 text-blue-500" />
            </div>
          </div>

          {/* MRR */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Monthly Recurring Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(summary?.mrr || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {Number(mrrGrowth) > 0 ? '+' : ''}{mrrGrowth}% vs last month
                </p>
              </div>
              <DollarSign className="h-12 w-12 text-green-500" />
            </div>
          </div>

          {/* Active Subscriptions */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active Subscriptions</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(summary?.active_subscriptions || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  {formatNumber(summary?.paid_subscriptions || 0)} paid
                </p>
              </div>
              <UserCheck className="h-12 w-12 text-purple-500" />
            </div>
          </div>

          {/* Daily Active Users */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Daily Active Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(summary?.daily_active_users || 0)}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Today's usage
                </p>
              </div>
              <Activity className="h-12 w-12 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Alerts */}
        {(summary?.pending_plan_changes || 0) > 0 && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  {summary?.pending_plan_changes} pending plan changes and {summary?.recent_cancellations || 0} recent cancellations
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Revenue Trends */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={revenueChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip formatter={(value: any) => formatCurrency(Number(value))} />
                <Legend />
                <Line type="monotone" dataKey="revenue" stroke="#8884d8" name="Total Revenue" />
                <Line type="monotone" dataKey="personal" stroke="#82ca9d" name="Personal" />
                <Line type="monotone" dataKey="business" stroke="#ffc658" name="Business" />
                <Line type="monotone" dataKey="enterprise" stroke="#ff7c7c" name="Enterprise" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Subscription Distribution */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Subscription Distribution</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={subscriptionPieData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {subscriptionPieData.map((_entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Usage by Plan */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Plan</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={usageChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="plan" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="messages" fill="#8884d8" name="Messages" />
                <Bar dataKey="video" fill="#82ca9d" name="Video (min)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Subscription Analytics Table */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Plan Analytics</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Subscribers</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">MRR</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Churn</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {subscription_analytics.map((plan) => (
                    <tr key={plan.plan_id}>
                      <td className="px-4 py-2 text-sm text-gray-900">{plan.plan_name}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{plan.subscriber_count}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(plan.mrr_contribution)}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">
                        {plan.subscriber_count > 0 
                          ? `${((plan.cancelled_count / plan.subscriber_count) * 100).toFixed(1)}%`
                          : '0%'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
}